import React from "react";

import {
  NO_DATA_STATUS_VALUE,
  type StackedStatusBarConfig,
  type StatusColor,
  type StatusSegment,
} from "./chartData";

export interface StackedStatusBarProps {
  readonly config: StackedStatusBarConfig;
  readonly onSegmentClick: (segment: StatusSegment) => void;
  /**
   * Segments whose `statusValue` is in this set render at full opacity;
   * others in this bar dim. Undefined/empty means no selection (all
   * bright).
   */
  readonly activeStatusValues?: ReadonlySet<string>;
}

const COLOR_BY_STATUS: Record<StatusColor, string> = {
  success: "var(--color-charts-status-positive, #037f0c)",
  "success-light": "#7cb87f",
  error: "var(--color-charts-status-critical, #d91515)",
  info: "var(--color-charts-status-info, #0972d3)",
  grey: "var(--color-charts-status-neutral, #5f6b7a)",
};

const MIN_LABEL_PERCENTAGE = 6;

const ROOT_STYLE: React.CSSProperties = {
  width: "100%",
};

const TITLE_STYLE: React.CSSProperties = {
  fontWeight: 700,
  fontSize: "1.4375rem",
  color: "var(--color-text-heading-default, #000716)",
  marginBottom: "0.625rem",
};

const BAR_STYLE: React.CSSProperties = {
  display: "flex",
  width: "100%",
  height: "2.6875rem",
  borderRadius: "5px",
  overflow: "hidden",
  boxSizing: "border-box",
};

const SEGMENT_LABEL_STYLE: React.CSSProperties = {
  color: "#ffffff",
  fontSize: "1.25rem",
  fontWeight: 700,
  lineHeight: 1,
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.35)",
  whiteSpace: "nowrap",
  pointerEvents: "none",
  userSelect: "none",
};

function buildSegmentAriaLabel(
  config: StackedStatusBarConfig,
  segment: StatusSegment
): string {
  return (
    `${config.title} ${segment.label}: ` +
    `${segment.count} of ${config.total} ASINs, ` +
    `${segment.percentage} percent — click to filter`
  );
}

function buildTooltip(segment: StatusSegment): string {
  return `${segment.label}: ${segment.count} ASINs (${segment.percentage}%)`;
}

const DIMMED_SEGMENT_OPACITY = 0.35;

function renderZeroSegment(
  config: StackedStatusBarConfig,
  segment: StatusSegment
): JSX.Element {
  return (
    <div
      key={segment.statusValue}
      data-testid={`status-segment-${config.key}-${segment.statusValue}`}
      aria-hidden="true"
      style={{
        flex: "0 0 0%",
        width: 0,
        height: "100%",
        backgroundColor: COLOR_BY_STATUS[segment.color],
      }}
    />
  );
}

function renderNonInteractiveSegment(
  config: StackedStatusBarConfig,
  segment: StatusSegment,
  activeStatusValues: ReadonlySet<string> | undefined
): JSX.Element {
  const ariaLabel =
    `${config.title} ${segment.label}: ` +
    `${segment.count} of ${config.total} ASINs, ` +
    `${segment.percentage} percent`;
  const testId = `status-segment-${config.key}-${segment.statusValue}`;
  const showLabel = segment.percentage >= MIN_LABEL_PERCENTAGE;
  const isDimmed =
    activeStatusValues !== undefined && activeStatusValues.size > 0;

  return (
    <div
      key={segment.statusValue}
      role="img"
      aria-label={ariaLabel}
      data-testid={testId}
      data-interactive="false"
      title={buildTooltip(segment)}
      style={{
        flex: `0 0 ${segment.percentage}%`,
        minWidth: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: COLOR_BY_STATUS[segment.color],
        overflow: "hidden",
        opacity: isDimmed ? DIMMED_SEGMENT_OPACITY : 1,
        transition: "opacity 120ms ease-out",
      }}
    >
      {showLabel && (
        <span style={SEGMENT_LABEL_STYLE}>{segment.percentage}%</span>
      )}
    </div>
  );
}

function renderInteractiveSegment(
  config: StackedStatusBarConfig,
  segment: StatusSegment,
  onSegmentClick: (segment: StatusSegment) => void,
  activeStatusValues: ReadonlySet<string> | undefined
): JSX.Element {
  const activate = () => onSegmentClick(segment);
  const ariaLabel = buildSegmentAriaLabel(config, segment);
  const testId = `status-segment-${config.key}-${segment.statusValue}`;
  const showLabel = segment.percentage >= MIN_LABEL_PERCENTAGE;

  const hasSelection =
    activeStatusValues !== undefined && activeStatusValues.size > 0;
  const isActive = hasSelection && activeStatusValues!.has(segment.statusValue);
  const isDimmed = hasSelection && !isActive;
  const ariaPressed = hasSelection ? isActive : undefined;

  return (
    <div
      key={segment.statusValue}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-pressed={ariaPressed}
      data-testid={testId}
      data-active={isActive ? "true" : undefined}
      title={buildTooltip(segment)}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          activate();
        }
      }}
      style={{
        flex: `0 0 ${segment.percentage}%`,
        minWidth: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: COLOR_BY_STATUS[segment.color],
        cursor: "pointer",
        overflow: "hidden",
        opacity: isDimmed ? DIMMED_SEGMENT_OPACITY : 1,
        transition: "opacity 120ms ease-out",
      }}
    >
      {showLabel && (
        <span style={SEGMENT_LABEL_STYLE}>{segment.percentage}%</span>
      )}
    </div>
  );
}

const EMPTY_BAR_STYLE: React.CSSProperties = {
  ...BAR_STYLE,
  backgroundColor: COLOR_BY_STATUS.grey,
  alignItems: "center",
  justifyContent: "center",
};

const EMPTY_BAR_LABEL_STYLE: React.CSSProperties = {
  ...SEGMENT_LABEL_STYLE,
  fontSize: "1rem",
};

export const StackedStatusBar: React.FC<StackedStatusBarProps> = ({
  config,
  onSegmentClick,
  activeStatusValues,
}) => {
  const isEmpty = config.total === 0;
  return (
    <div style={ROOT_STYLE} data-testid={`stacked-status-bar-${config.key}`}>
      <div style={TITLE_STYLE}>{config.title}</div>
      {isEmpty ? (
        <div
          role="img"
          aria-label={`${config.title}: no data`}
          data-testid={`stacked-status-bar-${config.key}-empty`}
          style={EMPTY_BAR_STYLE}
        >
          <span style={EMPTY_BAR_LABEL_STYLE}>No Data</span>
        </div>
      ) : (
        <div
          role="group"
          aria-label={`${config.title}: ${config.total} ASINs total`}
          style={BAR_STYLE}
        >
          {config.segments.map((segment) => {
            if (segment.count === 0) {
              return renderZeroSegment(config, segment);
            }
            if (segment.statusValue === NO_DATA_STATUS_VALUE) {
              return renderNonInteractiveSegment(
                config,
                segment,
                activeStatusValues
              );
            }
            return renderInteractiveSegment(
              config,
              segment,
              onSegmentClick,
              activeStatusValues
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StackedStatusBar;
